import { useEffect } from "react";

interface PageMeta {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
}

const BASE_URL = "https://www.elfazaa.com";
const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/dJyMtgD3h1SEdLgSAb7aIf2SO392/social-images/social-1774312449401-branding.webp";
const SITE_NAME = "EL FAZA";

export function usePageMeta({ title, description, canonical, ogImage }: PageMeta) {
  useEffect(() => {
    // Title
    document.title = title;

    // Helper to set or create a meta tag
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("name", "description", description);

    // Open Graph
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical ? `${BASE_URL}${canonical}` : BASE_URL);
    setMeta("property", "og:image", ogImage || DEFAULT_OG_IMAGE);

    // Twitter
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage || DEFAULT_OG_IMAGE);

    // Canonical link
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    const canonicalPath = canonical || "/";
    const canonicalUrl = `${BASE_URL}${canonicalPath === "/" ? "" : canonicalPath}` || BASE_URL;
    link.setAttribute("href", canonicalUrl);

    // Compute FR ↔ EN equivalents so hreflang tags point to the correct localized URL.
    // Static page mapping (FR path → EN path). Product slugs are shared FR/EN.
    const FR_TO_EN: Record<string, string> = {
      "/": "/en",
      "/notre-histoire": "/en/our-story",
      "/contact": "/en/contact",
      "/install": "/en/install",
      "/merci": "/en/thank-you",
    };
    const EN_TO_FR: Record<string, string> = Object.fromEntries(
      Object.entries(FR_TO_EN).map(([fr, en]) => [en, fr]),
    );

    const isEnPath = canonicalPath === "/en" || canonicalPath.startsWith("/en/");
    let frPath = canonicalPath;
    let enPath = canonicalPath;
    if (isEnPath) {
      frPath = EN_TO_FR[canonicalPath] ?? (canonicalPath.replace(/^\/en/, "") || "/");
      enPath = canonicalPath;
    } else {
      frPath = canonicalPath;
      enPath = FR_TO_EN[canonicalPath] ?? `/en${canonicalPath}`;
    }
    const frUrl = `${BASE_URL}${frPath === "/" ? "" : frPath}` || BASE_URL;
    const enUrl = `${BASE_URL}${enPath}`;

    // Hreflang alternates — FR locales → FR URL, EN locales → EN URL, x-default → FR.
    const HREFLANG_MAP: Array<[string, string]> = [
      ["fr-CA", frUrl],
      ["fr-FR", frUrl],
      ["fr-TN", frUrl],
      ["fr", frUrl],
      ["en-CA", enUrl],
      ["en-US", enUrl],
      ["en", enUrl],
      ["x-default", frUrl],
    ];
    HREFLANG_MAP.forEach(([lang, href]) => {
      let alt = document.querySelector(
        `link[rel="alternate"][hreflang="${lang}"]`,
      ) as HTMLLinkElement | null;
      if (!alt) {
        alt = document.createElement("link");
        alt.setAttribute("rel", "alternate");
        alt.setAttribute("hreflang", lang);
        document.head.appendChild(alt);
      }
      alt.setAttribute("href", href);
    });

    // Cleanup: restore defaults on unmount
    return () => {
      document.title = `${SITE_NAME} — Adhésifs 3D Tunisiens | Fierté & Racines`;
      const defaultDesc =
        "Des adhésifs en relief qui portent en eux la fierté de nos racines et la gratitude envers le Canada 🍁 — le pays qui nous a tout donné pour réussir.";
      setMeta("name", "description", defaultDesc);
      setMeta("property", "og:title", document.title);
      setMeta("property", "og:description", defaultDesc);
      setMeta("property", "og:url", BASE_URL + "/");
      setMeta("property", "og:image", DEFAULT_OG_IMAGE);
      setMeta("name", "twitter:title", document.title);
      setMeta("name", "twitter:description", defaultDesc);
      if (link) link.setAttribute("href", BASE_URL + "/");
    };
  }, [title, description, canonical, ogImage]);
}
